// The kanta/salkku overview's building tiles — same chrome and salkku grouping as the generic
// BuildingList (grouped salkku sections on kanta, flat grid on a salkku or when no salkku exists),
// but each tile's badge is the lens-agnostic `PerBuildingOverview` (which of this lens's
// questions/buckets are still open, or complete) instead of Optimoitu / €/kk / active-optimization
// count (docs/spec/concepts.md §2.4 → "Portfolio-scope overview grammar"). Generic
// BuildingList is not reused here — it reads `useDashboardStats()` and renders optimization
// content no overview lens (setup or judgment) may show.
//
// A building with open questions carries one small tone chip per open question — labelled and
// toned identically to that question's situation card (`OverviewBucket.label` / `.tone`) — so a
// tile's detail always agrees with the cards above it.
import { useState } from "react";
import { ArrowUpRight, Layers, CheckCircle2, HelpCircle } from "lucide-react";

import { useOpenBuilding } from "@/lib/open-building";
import { cardTonePill, moduleCard, type CardTone } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { cn } from "@/lib/utils";
import { useInventoryFilter } from "@/lib/inventory-filter";
import type { Building, BuildingGroup } from "@/lib/api/building";
import type { OverviewBucket, PerBuildingOverview } from "@/lib/overview-lens";
import { tileStatus } from "@/components/control-tower/perspectives/overview-tile-status";

const MAX_INDIVIDUAL = 8;
const MAX_PER_GROUP = 4;

// Buildings still needing attention surface first, then unresolved/unknown, then complete last —
// mirrors the home tile grid putting buildings needing attention first.
function overviewRank(overview: PerBuildingOverview | undefined): number {
  if (!overview) return 0;
  if (!overview.dataAvailable) return 1;
  if (!overview.complete) return 0;
  return 2;
}

function TileBadge({
  overview,
  completeLabel,
  bucketByKey,
}: {
  overview: PerBuildingOverview | undefined;
  completeLabel: string;
  bucketByKey: Map<string, OverviewBucket>;
}) {
  const status = tileStatus(overview, completeLabel, bucketByKey);

  if (status.kind === "open") {
    return (
      <div className="relative flex flex-wrap gap-1">
        {status.chips.map((chip) => {
          const tone: CardTone = bucketByKey.get(chip.key)?.tone ?? "warn";
          return (
            <span
              key={chip.key}
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
                cardTonePill[tone],
              )}
            >
              {chip.label}
            </span>
          );
        })}
      </div>
    );
  }

  const tone: CardTone = status.kind === "complete" ? "ok" : "empty";
  const Icon =
    status.kind === "complete"
      ? CheckCircle2
      : status.kind === "unknown"
        ? HelpCircle
        : null;
  return (
    <span
      className={cn(
        "relative inline-flex items-center gap-1 self-start rounded-full border px-2 py-0.5 text-[10px] font-medium",
        cardTonePill[tone],
      )}
    >
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {status.chips[0].label}
    </span>
  );
}

function BuildingTile({
  building,
  overview,
  completeLabel,
  bucketByKey,
}: {
  building: Building;
  overview: PerBuildingOverview | undefined;
  completeLabel: string;
  bucketByKey: Map<string, OverviewBucket>;
}) {
  const openBuilding = useOpenBuilding();

  return (
    <button
      type="button"
      onClick={() => openBuilding(building.id)}
      className={cn(moduleCard, "p-4 text-left flex flex-col gap-3 w-full")}
    >
      <CardGlowOverlay />

      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug truncate">
            {building.name}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {building.city} · {building.building_type}
          </p>
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
      </div>

      <TileBadge
        overview={overview}
        completeLabel={completeLabel}
        bucketByKey={bucketByKey}
      />
    </button>
  );
}

function TileGrid({
  buildings,
  perBuilding,
  completeLabel,
  bucketByKey,
}: {
  buildings: Building[];
  perBuilding: Map<string, PerBuildingOverview>;
  completeLabel: string;
  bucketByKey: Map<string, OverviewBucket>;
}) {
  const sorted = [...buildings].sort(
    (a, b) =>
      overviewRank(perBuilding.get(a.id)) - overviewRank(perBuilding.get(b.id)),
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {sorted.map((b) => (
        <BuildingTile
          key={b.id}
          building={b}
          overview={perBuilding.get(b.id)}
          completeLabel={completeLabel}
          bucketByKey={bucketByKey}
        />
      ))}
    </div>
  );
}

/** Same Näytä lisää / Näytä vähemmän control as etusivu BuildingList — not dead “+N piilotettu”. */
function ExpandableTileGrid({
  buildings,
  initialLimit,
  perBuilding,
  completeLabel,
  bucketByKey,
}: {
  buildings: Building[];
  initialLimit: number;
  perBuilding: Map<string, PerBuildingOverview>;
  completeLabel: string;
  bucketByKey: Map<string, OverviewBucket>;
}) {
  const [expanded, setExpanded] = useState(false);
  const hidden = Math.max(0, buildings.length - initialLimit);
  const visible = expanded ? buildings : buildings.slice(0, initialLimit);

  return (
    <div className="space-y-2">
      <TileGrid
        buildings={visible}
        perBuilding={perBuilding}
        completeLabel={completeLabel}
        bucketByKey={bucketByKey}
      />
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-medium text-primary hover:underline"
        >
          {expanded
            ? "Näytä vähemmän"
            : `Näytä lisää (${hidden} kiinteistöä)`}
        </button>
      )}
    </div>
  );
}

function GroupSection({
  group,
  buildings,
  perBuilding,
  completeLabel,
  bucketByKey,
}: {
  group: BuildingGroup;
  buildings: Building[];
  perBuilding: Map<string, PerBuildingOverview>;
  completeLabel: string;
  bucketByKey: Map<string, OverviewBucket>;
}) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
          <Layers className="h-3.5 w-3.5 shrink-0" />
          {group.name}
          <span className="font-normal normal-case tracking-normal text-[11px]">
            · {buildings.length} kiinteistöä
          </span>
        </h3>
      </div>
      <ExpandableTileGrid
        buildings={buildings}
        initialLimit={MAX_PER_GROUP}
        perBuilding={perBuilding}
        completeLabel={completeLabel}
        bucketByKey={bucketByKey}
      />
    </section>
  );
}

export function OverviewBuildingList({
  scopedBuildings,
  perBuilding,
  completeLabel,
  buckets,
}: {
  scopedBuildings: Building[];
  perBuilding: Map<string, PerBuildingOverview>;
  /** "Valmis" for Konfiguraatio, "Kunnossa" for the judgment lenses. */
  completeLabel: string;
  /** Labels/tones an open question's chip — the same buckets the situation cards render, so a
   *  tile's chips always agree with the cards above them. */
  buckets: OverviewBucket[];
}) {
  const { groupId, activeGroup, groups } = useInventoryFilter();
  const bucketByKey = new Map(buckets.map((b) => [b.key, b]));

  if (scopedBuildings.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Ei kiinteistöjä valituilla rajauksilla.
      </p>
    );
  }

  // ── A salkku is selected: flat grid over that salkku's (already scoped) buildings ──────────
  if (groupId !== null && activeGroup !== null) {
    return (
      <div className="space-y-3">
        <ExpandableTileGrid
          buildings={scopedBuildings}
          initialLimit={MAX_INDIVIDUAL}
          perBuilding={perBuilding}
          completeLabel={completeLabel}
          bucketByKey={bucketByKey}
        />
      </div>
    );
  }

  // ── Kiinteistökanta, salkkuja olemassa: grouped sections + "Ei salkussa" ────────────────────
  if (groups.length > 0) {
    const groupedIds = new Set(groups.flatMap((g) => g.building_ids));
    const sections = groups
      .map((group) => ({
        group,
        buildings: scopedBuildings.filter((b) =>
          group.building_ids.includes(b.id),
        ),
      }))
      .filter((s) => s.buildings.length > 0);
    const ungrouped = scopedBuildings.filter((b) => !groupedIds.has(b.id));

    return (
      <div className="space-y-6">
        {sections.map(({ group, buildings }) => (
          <GroupSection
            key={group.id}
            group={group}
            buildings={buildings}
            perBuilding={perBuilding}
            completeLabel={completeLabel}
            bucketByKey={bucketByKey}
          />
        ))}

        {ungrouped.length > 0 && (
          <section>
            <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">
              Ei salkussa
              <span className="font-normal normal-case tracking-normal text-[11px]">
                · {ungrouped.length} kiinteistöä
              </span>
            </h3>
            <ExpandableTileGrid
              buildings={ungrouped}
              initialLimit={MAX_PER_GROUP}
              perBuilding={perBuilding}
              completeLabel={completeLabel}
              bucketByKey={bucketByKey}
            />
          </section>
        )}
      </div>
    );
  }

  // ── No salkut defined at all: flat grid ─────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      <ExpandableTileGrid
        buildings={scopedBuildings}
        initialLimit={MAX_INDIVIDUAL}
        perBuilding={perBuilding}
        completeLabel={completeLabel}
        bucketByKey={bucketByKey}
      />
    </div>
  );
}
